import React from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { connect } from 'react-redux';
import { Field } from 'redux-form';
import { getClassName, checkButtonDisabled } from '../../utils/forms';
import { storeWithExpiration, toIdString } from '../../utils/common';
import { validationError } from '../../actions/order';
import { dismissError } from '../../actions/common';
import { UCT_FULL_FILE, UCT_SEGMENT_BUILDER } from '../../config/app_config';

import InfoIcon from '../shared/InfoIcon';

const USE_CASES = ['BULK_APPEND', 'LIST_BUILDING', 'SINGLE_RECORD_APPEND', 'FULL_FILE_INSTALL'];

export class NewOrderFormThree extends React.Component {
  static propTypes = {
    addToListFunction: PropTypes.func.isRequired,
    cancelOrderFunction: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
    checkedArray: PropTypes.array.isRequired,
    clearUseCasesFunction: PropTypes.func.isRequired,
    dataSets: PropTypes.object.isRequired,
    dismissErrorAction: PropTypes.func.isRequired,
    escapeForm: PropTypes.func.isRequired,
    fetchFieldsetsByIdFunction: PropTypes.func.isRequired,
    fieldSets: PropTypes.object.isRequired,
    formValues: PropTypes.object.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    invalid: PropTypes.bool.isRequired,
    platformMap: PropTypes.object.isRequired,
    previousPageFunction: PropTypes.func.isRequired,
    renderCompanyInfo: PropTypes.func.isRequired,
    submitFunction: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
    useCaseMap: PropTypes.object.isRequired,
    validationErrorAction: PropTypes.func.isRequired,
  };

  componentWillMount = () => {
    this.refreshUseCases();
  };

  componentWillReceiveProps = nextProps => {
    const {
      formValues: { platformCode },
    } = this.props;
    // If they switch platforms update state (in case some check boxes got removed)
    if (platformCode !== undefined && nextProps.formValues.platformCode !== platformCode) {
      this.resetUseCases();
    }
  };

  refreshUseCases = () => {
    const { formValues, addToListFunction, checkedArray } = this.props;

    // Initialize local state checked and removed arrays if user leaves page and comes back
    if (checkedArray.length < 1) {
      USE_CASES.forEach(useCase => {
        addToListFunction(useCase, formValues[useCase], checkedArray);
      });
    }
  };

  resetUseCases = () => {
    const { change, checkedArray, clearUseCasesFunction } = this.props;
    // First remove use cases from the redux form
    checkedArray.forEach(useCase => {
      change(useCase, null);
    });
    // Then clear
    clearUseCasesFunction();
  };

  validateSubmitFunction = (resolve, reject) => {
    const { checkedArray, dismissErrorAction } = this.props;

    if (checkedArray.length === 0) {
      reject('Please Select at Least One Use Case');
    }
    dismissErrorAction();
    resolve();
  };

  renderPlatformSelect = ({ input, label, meta: { touched, error } }) => {
    const { platformMap } = this.props;
    return (
      <div className={getClassName(touched, error)}>
        <label htmlFor="platform-select" className="form-control-label">
          {label} {touched && error && <span> | {error} </span>}
        </label>
        <div className="form-group row">
          <div className="col-md-6">
            <select {...input} id="platform-select" className="form-control form-control-success">
              <option value="">&lt; Please Select &gt;</option>
              {platformMap.valueSeq().map(this.renderItem)}
            </select>
          </div>
        </div>
      </div>
    );
  };

  renderItem = item => (
    <option key={item.code} value={item.code}>
      {item.description}
    </option>
  );

  renderUseCase = useCase => {
    const {
      addToListFunction,
      checkedArray,
      formValues: { useCaseType },
    } = this.props;
    let disable = true;

    if (useCaseType !== undefined) {
      const fullInstallVal = checkedArray.indexOf('FULL_FILE_INSTALL');
      disable = useCase.code !== 'FULL_FILE_INSTALL' && fullInstallVal >= 0;
    }

    return (
      <p key={useCase.code}>
        <Field
          name={useCase.code}
          component="input"
          type="checkbox"
          className={disable ? 'checkBox disabled' : 'checkBox'}
          onChange={event => {
            const { checked } = event.target;
            addToListFunction(useCase.code, checked, checkedArray);
          }}
          disabled={disable}
          checked={checkedArray.indexOf(useCase.code) >= 0}
        />
        &nbsp;
        <span className={disable ? 'text-muted' : null}>
          {useCase.code === 'FULL_FILE_INSTALL' ? 'File Install' : useCase.description}
          &nbsp;
          <InfoIcon infoMessage={useCase.definition} />
        </span>
      </p>
    );
  };

  renderGenericField = ({ input, label, meta: { touched, error } }) => (
    <div className={getClassName(touched, error)}>
      <label htmlFor={toIdString(label)} className="form-control-label">
        {label} {touched && error && <span> | {error} </span>}
      </label>
      {this.renderSpecialInput(label, input)}
    </div>
  );

  renderSpecialInput = (label, input) => {
    if (label === 'List') {
      return this.renderListIdSelect(input, label);
    } else if (label === 'SKU') {
      return this.renderSkuIdSelect(input, label);
    }
    return null;
  };

  renderListIdSelect = (input, label) => {
    const { change, fetchFieldsetsByIdFunction, dataSets } = this.props;
    return (
      <select
        {...input}
        id={toIdString(label)}
        onChange={event => {
          input.onChange(event);
          const dataset = JSON.parse(event.target.value);
          fetchFieldsetsByIdFunction(dataset.id, 'SKU', storeWithExpiration.get('token'));
          change('skuId', '');
        }}
        className="form-control form-control-success"
      >
        <option value="">&lt; Please Select &gt;</option>
        {dataSets.size > 0 ? dataSets.valueSeq().map(this.renderDataSetItem) : null}
      </select>
    );
  };

  renderSkuIdSelect = (input, label) => {
    const { fieldSets } = this.props;
    return (
      <select {...input} id={toIdString(label)} className="form-control form-control-success">
        <option value="">&lt; Please Select &gt;</option>
        {fieldSets.size > 0 ? (
          fieldSets.valueSeq().map(this.renderDataItem)
        ) : (
          <option value="-1">No SKUs found</option>
        )}
      </select>
    );
  };

  // Only render lists that are live (pending lists don't have SKUs)
  renderDataSetItem = item => (item.status !== 0 ? this.renderDataItem(item) : null);

  renderDataItem = item => (
    <option key={item.id} value={JSON.stringify(item)}>
      {item.name}
    </option>
  );

  renderInfoTooltip = () => (
    <ReactTooltip
      id="infoIcon"
      place="right"
      effect="solid"
      delayHide={0}
      offset={{ top: 8 }}
      className="tip"
      type="light"
      border
    />
  );

  render() {
    const {
      escapeForm,
      invalid,
      previousPageFunction,
      renderCompanyInfo,
      submitFunction,
      submitting,
      useCaseMap,
      validationErrorAction,
      formValues: { platformCode },
    } = this.props;

    return (
      <div className="modal-wrap">
        <div className="container-fluid container-home">
          <div className="header">
            <div className="header-body align-items-center">
              <div className="row">
                <div className="col-sm-12">
                  <span className="pull-right">
                    <button
                      type="button"
                      className="btn btn-danger-outline"
                      onClick={() => escapeForm()}
                    >
                      Cancel
                    </button>
                  </span>
                  <h1 className="header-title">Data Setup</h1>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <div className="card-border">
                <div className="card-header">
                  <div className="card-header-title">Order Data Configuration</div>
                </div>
                <div className="card-block">
                  {renderCompanyInfo()}
                  <form className="small-form">
                    <fieldset>
                      <div className="card card-block">
                        <div className="form-group">
                          <div className="row">
                            <div className="col-sm-12">
                              <label
                                htmlFor="prematched-radio-group"
                                className="form-control-label"
                              >
                                Would you like to use pre-matched data? <br />
                                <small>
                                  (This requires you to upload CSV files with provider record IDs
                                  and end user CRM record IDs. You will be prompted to upload files
                                  after the order is submitted.)
                                </small>
                              </label>
                              <br />
                              <div id="prematched-radio-group" className="radio-inline">
                                <label htmlFor="yes-radio" className="form-control-label">
                                  <Field
                                    name="matchedPairsProvided"
                                    value="true"
                                    component="input"
                                    type="radio"
                                    id="yes-radio"
                                  />{' '}
                                  Yes
                                </label>
                              </div>
                              <div className="radio-inline">
                                <label htmlFor="no-radio" className="form-control-label">
                                  <Field
                                    name="matchedPairsProvided"
                                    value="false"
                                    component="input"
                                    type="radio"
                                    id="no-radio"
                                  />{' '}
                                  No
                                </label>
                              </div>
                              <label
                                htmlFor="endusercredentials-radio-group"
                                className="form-control-label"
                              >
                                Create records using clients credential? (Salesforce)
                                <br />
                                <small>
                                  (Enabling this option will populate the Created By field in
                                  Salesforce with the individual who created the record. Leaving
                                  this as No will populate the field with the user who installed the
                                  app. NOTE: This can only be set to Yes if the admin grants
                                  privileges to the end user to create Accounts, Contacts, Leads and
                                  the associated custom objects, and disables de-duplication rules
                                  per user.)
                                </small>
                              </label>
                              <br />
                              <div id="endusercredentials-radio-group" className="radio-inline">
                                <label htmlFor="yes-radio" className="form-control-label">
                                  <Field
                                    name="useEndUserCredentials"
                                    value="true"
                                    component="input"
                                    type="radio"
                                    id="yes-radio"
                                  />{' '}
                                  Yes
                                </label>
                              </div>
                              <div className="radio-inline">
                                <label htmlFor="no-radio" className="form-control-label">
                                  <Field
                                    name="useEndUserCredentials"
                                    value="false"
                                    component="input"
                                    type="radio"
                                    id="no-radio"
                                  />{' '}
                                  No
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="form-group">
                          <div className="row">
                            <div className="col-sm-12">
                              <label
                                htmlFor="contactmatch-radio-group"
                                className="form-control-label"
                              >
                                Would you like contacts to be matched within the matched account
                                only? <br />
                                <small>
                                  (For Hubspot it is recommended that you set this feature to false,
                                  as the instance may have contacts without accounts.)
                                </small>
                              </label>
                              <br />
                              <div id="contactmatch-radio-group" className="radio-inline">
                                <label htmlFor="yes-radio" className="form-control-label">
                                  <Field
                                    name="contactMatchByAccount"
                                    value="true"
                                    component="input"
                                    type="radio"
                                    id="yes-radio"
                                  />{' '}
                                  Yes
                                </label>
                              </div>
                              <div className="radio-inline">
                                <label htmlFor="no-radio" className="form-control-label">
                                  <Field
                                    name="contactMatchByAccount"
                                    value="false"
                                    component="input"
                                    type="radio"
                                    id="no-radio"
                                  />{' '}
                                  No
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="form-group">
                          <div className="row">
                            <div className="col-sm-12">
                              <label
                                htmlFor="csvdownload-radio-group"
                                className="form-control-label"
                              >
                                Allow CSV download in Prospecting? <br />
                              </label>
                              <br />
                              <div id="csvdownload-radio-group" className="radio-inline">
                                <label htmlFor="yes-radio" className="form-control-label">
                                  <Field
                                    name="csvDownloadEnabled"
                                    value="true"
                                    component="input"
                                    type="radio"
                                    id="yes-radio"
                                  />{' '}
                                  Yes
                                </label>
                              </div>
                              <div className="radio-inline">
                                <label htmlFor="no-radio" className="form-control-label">
                                  <Field
                                    name="csvDownloadEnabled"
                                    value="false"
                                    component="input"
                                    type="radio"
                                    id="no-radio"
                                  />{' '}
                                  No
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="form-group">
                          <div className="row">
                            <div className="col-sm-12">
                              <label htmlFor="leadmatch-radio-group" className="form-control-label">
                                Would you like leads to be matched and appended? (Salesforce or
                                Dynamics)
                                <br />
                              </label>
                              <br />
                              <div id="leadmatch-radio-group" className="radio-inline">
                                <label htmlFor="yes-radio" className="form-control-label">
                                  <Field
                                    name="leadMatchEnabled"
                                    value="true"
                                    component="input"
                                    type="radio"
                                    id="yes-radio"
                                  />{' '}
                                  Yes
                                </label>
                              </div>
                              <div className="radio-inline">
                                <label htmlFor="no-radio" className="form-control-label">
                                  <Field
                                    name="leadMatchEnabled"
                                    value="false"
                                    component="input"
                                    type="radio"
                                    id="no-radio"
                                  />{' '}
                                  No
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                        <hr />
                        <div className="form-group">
                          <p>Select List and its associated SKU from the dropdown below</p>
                          <Field name="listId" component={this.renderGenericField} label="List" />
                        </div>
                        <div className="form-group">
                          <p>Customer will receive data only in the selected SKU fields</p>
                          <Field name="skuId" component={this.renderGenericField} label="SKU" />
                        </div>
                        <hr />
                        <Field
                          name="platformCode"
                          component={this.renderPlatformSelect}
                          label="Platform"
                        />
                        {platformCode === undefined ? null : (
                          <div className="form-group">
                            <div className="row">
                              <div className="col-xs-12 col-md-6">
                                <label
                                  htmlFor="list-criteria-radio-group"
                                  className="form-control-label"
                                >
                                  List Criteria
                                </label>
                                <div id="list-criteria-radio-group" className="radio">
                                  <label htmlFor="radio-full-file" className="form-control-label">
                                    <Field
                                      name="useCaseType"
                                      value={UCT_FULL_FILE}
                                      component="input"
                                      type="radio"
                                      id="radio-full-file"
                                    />{' '}
                                    Full File
                                  </label>
                                </div>
                                <div className="radio">
                                  <label
                                    htmlFor="radio-partial-file"
                                    className="form-control-label"
                                  >
                                    <Field
                                      name="useCaseType"
                                      value={UCT_SEGMENT_BUILDER}
                                      component="input"
                                      type="radio"
                                      id="radio-partial-file"
                                    />{' '}
                                    Partial File
                                  </label>
                                </div>
                              </div>
                              <div className="col-xs-12 col-md-6">
                                <label
                                  htmlFor="radio-use-case-group"
                                  className="form-control-label"
                                >
                                  Use Cases
                                </label>
                                <div id="radio-use-case-group" className="group-compare">
                                  {this.renderInfoTooltip()}
                                  {useCaseMap.get(platformCode).map(this.renderUseCase)}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="form-buttons">
                          <p className="pull-right">
                            <button
                              type="button"
                              className="btn btn-secondary btn-back"
                              onClick={previousPageFunction}
                            >
                              Back
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => {
                                const prom = new Promise((resolve, reject) => {
                                  this.validateSubmitFunction(resolve, reject);
                                });
                                prom
                                  .then(() => {
                                    submitFunction();
                                  })
                                  .catch(error => {
                                    validationErrorAction(error);
                                  });
                              }}
                              disabled={checkButtonDisabled(invalid, submitting)}
                            >
                              Continue
                            </button>
                          </p>
                        </div>
                      </div>
                    </fieldset>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default connect(
  null,
  {
    dismissErrorAction: dismissError,
    validationErrorAction: validationError,
  }
)(NewOrderFormThree);
